import React from "react";
import { Users, ShieldCheck, ChevronDown, ExternalLink, Moon } from "lucide-react";
import { DISCORD_LINK } from "@/constants";

const HeroSection = () => {
  const mockFeedItems = [
    {
      ticker: "$AAPL",
      content: "Strong Q4 earnings with services revenue hitting new highs. Debt ratios remain within Shariah thresholds.",
      time: "2h ago",
      isHalal: true,
    },
    {
      ticker: "$OGDC",
      content: "Pakistan's largest E&P company showing solid fundamentals. Oil & Gas assets provide tangible backing.",
      time: "4h ago",
      isHalal: true,
    },
    {
      ticker: "$MSFT",
      content: "Cloud growth continues. Reviewing interest income ratios for compliance status update.",
      time: "6h ago",
      isHalal: false,
    },
    {
      ticker: "$LUCK",
      content: "Lucky Cement expansion into African markets. Strong balance sheet with minimal debt.",
      time: "8h ago",
      isHalal: true,
    },
  ];

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="min-h-screen hero-gradient pt-24 pb-16 md:pt-32 md:pb-24"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="space-y-8 animate-fade-in-up">
            <div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-ics-white leading-tight mb-6">
                Shariah-First Investing, Built for{" "}
                <span className="text-gradient">Serious Long-Term Investors.</span>
              </h1>
              <p className="text-lg md:text-xl text-ics-gray leading-relaxed max-w-xl">
                ICS is a private research community sharing Shariah-compliant, long-term ideas in US and Pakistan stocks — without providing financial advice.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href={DISCORD_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-join-discord-btn"
                className="cta-primary inline-flex items-center justify-center gap-2 bg-ics-orange hover:bg-ics-orange-hover text-white font-semibold px-8 py-4 rounded-full text-lg transition-all duration-300 hover:scale-105 animate-pulse-glow"
              >
                Join the Discord Community
                <ExternalLink className="w-5 h-5" />
              </a>
              <a
                href="#research"
                data-testid="hero-research-btn"
                className="inline-flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 text-ics-white border border-white/10 hover:border-white/20 font-semibold px-8 py-4 rounded-full text-lg transition-all duration-300"
              >
                See How We Research Stocks
                <ChevronDown className="w-5 h-5" />
              </a>
            </div>

            <div className="flex items-center gap-8 pt-4">
              <div className="flex items-center gap-2 text-ics-gray">
                <Users className="w-5 h-5 text-ics-orange" />
                <span className="text-sm">Hundreds of Members</span>
              </div>
              <div className="flex items-center gap-2 text-ics-gray">
                <ShieldCheck className="w-5 h-5 text-ics-orange" />
                <span className="text-sm">Shariah-Focused</span>
              </div>
            </div>
          </div>

          <div className="animate-slide-in-right" style={{ animationDelay: "0.2s" }}>
            <div
              data-testid="mock-feed-card"
              className="glass-card rounded-2xl overflow-hidden"
            >
              <div className="bg-ics-slate/50 px-5 py-4 border-b border-white/5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full bg-ics-orange animate-pulse" />
                  <span className="font-body font-semibold text-ics-white">ICS Live Feed</span>
                </div>
                <span className="text-xs text-ics-gray">Research Ideas</span>
              </div>

              <div className="divide-y divide-white/5">
                {mockFeedItems.map((item, index) => (
                  <div key={index} className="mock-feed-item p-5">
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <span className="ticker-tag">{item.ticker}</span>
                      <div className="flex items-center gap-2">
                        {item.isHalal && (
                          <span className="halal-badge">
                            <Moon className="w-3 h-3" />
                            Compliant
                          </span>
                        )}
                        <span className="text-xs text-ics-gray">{item.time}</span>
                      </div>
                    </div>
                    <p className="text-sm text-ics-gray leading-relaxed font-body">
                      {item.content}
                    </p>
                  </div>
                ))}
              </div>

              <div className="bg-ics-slate/30 px-5 py-3 flex items-center justify-between">
                <span className="text-xs text-ics-gray">Educational research only. Not investment advice.</span>
                <a
                  href={DISCORD_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-ics-orange hover:underline font-medium"
                >
                  View More
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
